/**
 * VoiceChatMode — Full-screen voice conversation with Bot Lara
 *
 * Two speech recognition backends:
 * A) Whisper (server) — private, ~2-4s latency, default
 * B) Web Speech API (Apple/Google) — fast, interim results, opt-in
 *
 * Flow:
 * 1. Mic open → user speaks → silence detection
 * 2. Audio sent to Whisper (or Web Speech API processes locally)
 * 3. Transcribed text sent as message
 * 4. Bot streams response → TTS reads aloud → mic reopens
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { X, Mic, MicOff, PhoneOff } from "lucide-react";
import { useChat } from "../hooks/useChat";
import { api } from "../lib/api";
import { ws } from "../lib/ws";
import styles from "./VoiceChatMode.module.css";

interface VoiceChatModeProps {
  channelId: string;
  currentUserId: string;
  botName: string;
  onClose: () => void;
  onSendMessage: (text: string) => void;
}

// Silence detection thresholds
const SILENCE_THRESHOLD = 0.02; // Audio level below this = silence
const SILENCE_DURATION_MS = 1500; // How long silence before we stop
const MIN_RECORDING_MS = 500; // Minimum recording length

// Web Speech API types
interface SpeechRecognitionEvent {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}
interface SpeechRecognitionErrorEvent {
  error: string;
}
interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

const SpeechRecognitionClass =
  typeof window !== "undefined"
    ? ((window as unknown as Record<string, unknown>).SpeechRecognition || (window as unknown as Record<string, unknown>).webkitSpeechRecognition) as (new () => SpeechRecognitionInstance) | undefined
    : null;

// Strip markdown for cleaner TTS
function cleanForSpeech(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, "")
    .replace(/\*\*\*(.*?)\*\*\*/g, "$1")
    .replace(/\*\*(.*?)\*\*/g, "$1")
    .replace(/\*(.*?)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^#{1,3}\s+/gm, "")
    .replace(/^[-*]\s+/gm, "")
    .replace(/^\d+\.\s+/gm, "")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/https?:\/\/[^\s]+/g, "")
    .replace(/\n{2,}/g, ". ")
    .replace(/\n/g, " ")
    .trim();
}

function getGermanVoice(): SpeechSynthesisVoice | null {
  const voices = speechSynthesis.getVoices();
  const storedName = localStorage.getItem("tts-voice");
  if (storedName) {
    const stored = voices.find((v) => v.name === storedName);
    if (stored) return stored;
  }
  const preferred = ["Helena", "Anna", "Petra", "Google Deutsch", "Microsoft"];
  for (const name of preferred) {
    const match = voices.find((v) => v.lang.startsWith("de") && v.name.includes(name));
    if (match) return match;
  }
  return voices.find((v) => v.lang.startsWith("de")) ?? null;
}

type VoicePhase = "listening" | "processing" | "speaking" | "paused";
type RecognitionMode = "whisper" | "browser";

export function VoiceChatMode({ channelId, botName, onClose, onSendMessage }: VoiceChatModeProps) {
  const [phase, setPhase] = useState<VoicePhase>("listening");
  const [transcript, setTranscript] = useState("");
  const [interimText, setInterimText] = useState("");
  const [botText, setBotText] = useState("");
  const [audioLevel, setAudioLevel] = useState(0);
  const [mode] = useState<RecognitionMode>(() => {
    return (localStorage.getItem("voice-recognition-mode") as RecognitionMode) || "whisper";
  });

  const recognitionRef = useRef<any>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finalTranscriptRef = useRef("");
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const prevBotContentRef = useRef("");
  const isClosingRef = useRef(false);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number>(0);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingStartRef = useRef<number>(0);
  const silenceStartRef = useRef<number>(0);
  const phaseRef = useRef<VoicePhase>("listening");

  // Keep phaseRef in sync
  useEffect(() => { phaseRef.current = phase; }, [phase]);

  const { botStreams } = useChat();
  const botStream = botStreams.get(channelId);

  // ── Audio Level + Silence Detection (Whisper mode) ──
  const startWhisperListening = useCallback(() => {
    if (isClosingRef.current) return;

    setPhase("listening");
    setTranscript("");
    setInterimText("");
    silenceStartRef.current = 0;

    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      if (isClosingRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      
      streamRef.current = stream;

      // Audio context for level monitoring
      const ctx = new AudioContext();
      audioContextRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyserRef.current = analyser;
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.8;
      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);

      // MediaRecorder for capturing audio
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm"
        : MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      recordingStartRef.current = Date.now();

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.start(250); // Collect chunks every 250ms

      // Monitor audio levels + silence detection
      const dataArray = new Float32Array(analyser.fftSize);
      let speaking = false;

      const checkAudio = () => {
        if (isClosingRef.current || phaseRef.current !== "listening") return;

        analyser.getFloatTimeDomainData(dataArray);
        // RMS level
        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) sum += dataArray[i]! * dataArray[i]!;
        const rms = Math.sqrt(sum / dataArray.length);
        setAudioLevel(Math.min(rms * 5, 1)); // Amplify for visualization

        const now = Date.now();

        if (rms > SILENCE_THRESHOLD) {
          speaking = true;
          silenceStartRef.current = 0;
        } else if (speaking) {
          if (silenceStartRef.current === 0) {
            silenceStartRef.current = now;
          } else if (now - silenceStartRef.current >= SILENCE_DURATION_MS) {
            // Silence detected after speech — stop and transcribe
            const elapsed = now - recordingStartRef.current;
            if (elapsed >= MIN_RECORDING_MS) {
              stopAndTranscribe();
              return;
            }
          }
        }

        animFrameRef.current = requestAnimationFrame(checkAudio);
      };

      animFrameRef.current = requestAnimationFrame(checkAudio);
    }).catch((err) => {
      console.error("[VoiceChat] Mic access failed:", err);
      setPhase("paused");
    });
  }, []);

  const stopAndTranscribe = useCallback(async () => {
    setPhase("processing");
    setAudioLevel(0);

    // Stop animation frame
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);

    // Stop recorder and collect audio
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state === "inactive") {
      // Nothing recorded, go back to listening
      startWhisperListening();
      return;
    }

    const audioBlob = await new Promise<Blob>((resolve) => {
      recorder.onstop = () => {
        const mime = recorder.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        resolve(blob);
      };
      recorder.stop();
    });

    // Stop mic stream
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    audioContextRef.current?.close();
    audioContextRef.current = null;

    if (audioBlob.size < 500) {
      // Too short, restart listening
      if (!isClosingRef.current) startWhisperListening();
      return;
    }

    // Send to server for Whisper transcription
    try {
      const formData = new FormData();
      const ext = audioBlob.type.includes("mp4") ? "m4a" : "webm";
      formData.append("file", new File([audioBlob], `voice.${ext}`, { type: audioBlob.type }));

      const result = await api.upload<{ transcript: string }>("/voice/transcribe", formData);

      if (result.transcript) {
        setTranscript(result.transcript);
        onSendMessage(result.transcript);
      } else {
        // No speech detected, restart
        if (!isClosingRef.current) startWhisperListening();
      }
    } catch (err) {
      console.error("[VoiceChat] Transcription failed:", err);
      // Restart listening on error
      if (!isClosingRef.current) startWhisperListening();
    }
  }, [onSendMessage, startWhisperListening]);

  // ── Web Speech API (Browser mode) ──
  const startBrowserListening = useCallback(() => {
    if (!SpeechRecognitionClass || isClosingRef.current) return;

    setPhase("listening");
    setTranscript("");
    setInterimText("");

    // Start audio level visualization (separate from recognition)
    navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
      if (isClosingRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      streamRef.current = stream;
      const ctx = new AudioContext();
      audioContextRef.current = ctx;
      const analyser = ctx.createAnalyser();
      analyserRef.current = analyser;
      analyser.fftSize = 256;
      const source = ctx.createMediaStreamSource(stream);
      source.connect(analyser);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const update = () => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setAudioLevel(avg / 255);
        animFrameRef.current = requestAnimationFrame(update);
      };
      update();
    }).catch(() => {});

    const recognition = new SpeechRecognitionClass();
    recognition.lang = "de-DE";
    recognition.continuous = true;
    recognition.interimResults = true;

    finalTranscriptRef.current = "";

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let final = "";
      let interim = "";
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) final += result[0].transcript;
        else interim += result[0].transcript;
      }
      finalTranscriptRef.current = final;
      setTranscript(final);
      setInterimText(interim);

      // Reset silence timer
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => {
        const text = (finalTranscriptRef.current + interim).trim();
        if (text.length > 0) {
          recognition.stop();
          setPhase("processing");
          setTranscript(text);
          setInterimText("");
          onSendMessage(text);
        }
      }, SILENCE_DURATION_MS);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error === "not-allowed" || event.error === "service-not-available") {
        setPhase("paused");
      }
    };

    recognition.onend = () => {
      if (!isClosingRef.current && phaseRef.current === "listening") {
        try { recognition.start(); } catch { /* ignore */ }
      }
    };

    recognitionRef.current = recognition;
    try { recognition.start(); } catch { /* ignore */ }
  }, [onSendMessage]);

  // ── Unified start/stop ──
  const startListening = useCallback(() => {
    if (mode === "whisper") startWhisperListening();
    else startBrowserListening();
  }, [mode, startWhisperListening, startBrowserListening]);

  const stopAll = useCallback(() => {
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (recognitionRef.current) { recognitionRef.current.onend = null; recognitionRef.current.stop(); recognitionRef.current = null; }
    if (mediaRecorderRef.current?.state !== "inactive") { try { mediaRecorderRef.current?.stop(); } catch {} }
    mediaRecorderRef.current = null;
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    audioContextRef.current?.close();
    audioContextRef.current = null;
    setAudioLevel(0);
  }, []);

  // ── TTS ──
  // iOS Safari requires speechSynthesis to be "unlocked" by a user gesture.
  // We do this once on mount (triggered by the user's tap on the Phone button).
  const ttsUnlockedRef = useRef(false);

  const unlockTts = useCallback(() => {
    if (ttsUnlockedRef.current) return;
    ttsUnlockedRef.current = true;
    // Speak a silent/empty utterance to unlock the audio context
    const silent = new SpeechSynthesisUtterance("");
    silent.volume = 0;
    silent.rate = 10; // finish instantly
    speechSynthesis.speak(silent);
    // Also resume AudioContext if suspended (iOS)
    if (audioContextRef.current?.state === "suspended") {
      audioContextRef.current.resume();
    }
  }, []);

  const speakText = useCallback((text: string): Promise<void> => {
    return new Promise((resolve) => {
      const clean = cleanForSpeech(text);
      if (!clean) { resolve(); return; }

      // iOS: cancel any pending and re-speak
      speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(clean);
      utteranceRef.current = utterance;

      const voice = getGermanVoice();
      if (voice) { utterance.voice = voice; utterance.lang = voice.lang; }
      else { utterance.lang = "de-DE"; }
      utterance.rate = 1.05;
      utterance.pitch = 1.0;

      utterance.onend = () => { utteranceRef.current = null; resolve(); };
      utterance.onerror = () => { utteranceRef.current = null; resolve(); };

      // iOS Safari workaround: speechSynthesis can pause after ~15s
      // Keep it alive with a periodic resume
      const keepAlive = setInterval(() => {
        if (speechSynthesis.speaking && speechSynthesis.paused) {
          speechSynthesis.resume();
        }
        if (!speechSynthesis.speaking) {
          clearInterval(keepAlive);
        }
      }, 5000);

      utterance.onend = () => { clearInterval(keepAlive); utteranceRef.current = null; resolve(); };
      utterance.onerror = () => { clearInterval(keepAlive); utteranceRef.current = null; resolve(); };

      speechSynthesis.speak(utterance);
    });
  }, []);

  // ── Bot stream → TTS pipeline ──
  useEffect(() => {
    if (!botStream) return;
    if (botStream.isStreaming) {
      setPhase("speaking");
      setBotText(botStream.content);
    }
  }, [botStream?.content, botStream?.isStreaming]);

  // When stream ends → read aloud → restart mic.
  // Triggers on isStreaming=false (set by bot_stream_end) — the bubble may still be in
  // the botStreams map waiting for the persisted message (F11), but TTS shouldn't wait.
  useEffect(() => {
    const streamEnded = !botStream || botStream.isStreaming === false;
    if (streamEnded && prevBotContentRef.current && phase === "speaking") {
      const fullText = prevBotContentRef.current;
      prevBotContentRef.current = "";

      speakText(fullText).then(() => {
        if (!isClosingRef.current) {
          setBotText("");
          startListening();
        }
      });
    }
    if (botStream?.content) {
      prevBotContentRef.current = botStream.content;
    }
  }, [botStream, phase, speakText, startListening]);

  // ── Lifecycle ──
  useEffect(() => {
    // Notify server: voice mode ON (use fast model)
    ws.send({ type: "voice_mode", channelId, active: true });

    // Unlock TTS on mount (user gesture context from Phone button tap)
    unlockTts();
    speechSynthesis.getVoices();
    const h = () => speechSynthesis.getVoices();
    speechSynthesis.addEventListener?.("voiceschanged", h);
    startListening();

    return () => {
      isClosingRef.current = true;
      // Notify server: voice mode OFF
      ws.send({ type: "voice_mode", channelId, active: false });
      stopAll();
      speechSynthesis.cancel();
      speechSynthesis.removeEventListener?.("voiceschanged", h);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = () => {
    isClosingRef.current = true;
    stopAll();
    speechSynthesis.cancel();
    onClose();
  };

  const handleTogglePause = () => {
    if (phase === "paused") {
      startListening();
    } else if (phase === "listening") {
      stopAll();
      setPhase("paused");
    }
  };

  const pulseScale = phase === "listening" ? 1 + audioLevel * 0.4 : 1;

  return (
    <div className={styles.overlay}>
      {/* Close */}
      <button className={styles.closeBtn} onClick={handleClose}><X size={24} /></button>

      {/* Mode indicator */}
      <div className={styles.modeIndicator}>
        {mode === "whisper" ? "🔒 Privat" : "⚡ Schnell"}
      </div>

      {/* Center: Animated Orb */}
      <div className={styles.center}>
        <div
          className={`${styles.orb} ${styles[phase]}`}
          style={{ transform: `scale(${pulseScale})` }}
        >
          <div className={styles.orbInner}>
            {phase === "listening" && <Mic size={48} />}
            {phase === "processing" && <div className={styles.spinner} />}
            {phase === "speaking" && (
              <div className={styles.speakingWaves}>
                <span /><span /><span /><span /><span />
              </div>
            )}
            {phase === "paused" && <MicOff size={48} />}
          </div>
        </div>

        <div className={styles.statusText}>
          {phase === "listening" && "Ich höre zu..."}
          {phase === "processing" && "Verarbeite..."}
          {phase === "speaking" && botName}
          {phase === "paused" && "Pausiert"}
        </div>

        <div className={styles.textArea}>
          {(phase === "listening" || phase === "processing") && (transcript || interimText) && (
            <p className={styles.userText}>
              {transcript}
              {interimText && <span className={styles.interim}>{interimText}</span>}
            </p>
          )}
          {phase === "speaking" && botText && (
            <p className={styles.botResponseText}>{botText}</p>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className={styles.controls}>
        <button
          className={`${styles.controlBtn} ${phase === "paused" ? styles.muted : ""}`}
          onClick={handleTogglePause}
          title={phase === "paused" ? "Mikrofon aktivieren" : "Stummschalten"}
        >
          {phase === "paused" ? <MicOff size={20} /> : <Mic size={20} />}
        </button>

        <button className={`${styles.controlBtn} ${styles.hangup}`} onClick={handleClose} title="Beenden">
          <PhoneOff size={24} />
        </button>
      </div>
    </div>
  );
}
